import throttle from 'lodash/throttle';
import { useEffect, useState } from 'react';

interface NetworkInformation extends EventTarget {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface ExtendedNavigator extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

export interface NetworkStatus {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number;
  rtt?: number;
  online: boolean;
  isSupported: boolean;
  networkQuality: number;
  text: string;
  /** Online, but below the score the UI treats as unstable. */
  isPoorConnection: boolean;
}
/**
 * Hook to monitor network connection status using the Network Information API
 * Listens to connection changes and online/offline events
 */

const NETWORK_UPDATE_THROTTLE_MS = 3000;
export const POOR_NETWORK_THRESHOLD = 40;

export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    const nav = navigator as ExtendedNavigator;
    const connection =
      nav.connection || nav.mozConnection || nav.webkitConnection;

    const effectiveType = connection?.effectiveType;
    const downlink = connection?.downlink;
    const rtt = connection?.rtt;
    const online = navigator.onLine;
    const networkQuality = calculateNetworkQuality(
      effectiveType,
      downlink,
      rtt,
      online
    );
    const text = getNetworkQualityLabel(networkQuality);

    return {
      effectiveType,
      downlink,
      rtt,
      online,
      isSupported: !!connection,
      networkQuality,
      text,
      isPoorConnection: online && networkQuality < POOR_NETWORK_THRESHOLD,
    };
  });

  useEffect(() => {
    const nav = navigator as ExtendedNavigator;
    const connection =
      nav.connection || nav.mozConnection || nav.webkitConnection;

    const updateNetworkStatus = () => {
      const effectiveType = connection?.effectiveType;
      const downlink = connection?.downlink;
      const rtt = connection?.rtt;
      const online = navigator.onLine;
      const networkQuality = calculateNetworkQuality(
        effectiveType,
        downlink,
        rtt,
        online
      );
      const text = getNetworkQualityLabel(networkQuality);

      setStatus({
        effectiveType,
        downlink,
        rtt,
        online,
        isSupported: !!connection,
        networkQuality,
        text,
        isPoorConnection: online && networkQuality < POOR_NETWORK_THRESHOLD,
      });
    };

    const throttledUpdate = throttle(
      updateNetworkStatus,
      NETWORK_UPDATE_THROTTLE_MS,
      {
        leading: true,
        trailing: true,
      }
    );

    if (connection) {
      connection.addEventListener('change', throttledUpdate);
    }

    window.addEventListener('online', throttledUpdate);
    window.addEventListener('offline', throttledUpdate);

    return () => {
      // Cancel any pending throttled calls
      throttledUpdate.cancel();

      if (connection) {
        connection.removeEventListener('change', throttledUpdate);
      }
      window.removeEventListener('online', throttledUpdate);
      window.removeEventListener('offline', throttledUpdate);
    };
  }, []);

  return status;
}


function getNetworkQualityLabel(
  score: number
): 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Offline' {
  if (score === 0) return 'Offline';
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  return 'Poor';
}

function calculateNetworkQuality(
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g',
  downlink?: number,
  rtt?: number,
  online?: boolean
): number {
  if (!online) {
    return 0;
  }

  const baseScore = 50;

  if (!effectiveType && downlink === undefined && rtt === undefined) {
    return baseScore;
  }


  // Base score from effectiveType
  let score = baseScore;
  switch (effectiveType) {
    case '4g':
      score = 100;
      break;
    case '3g':
      score = 80;
      break;
    case '2g':
      score = 60;
      break;
    case 'slow-2g':
      score = 50;
      break;
  }

  if (downlink !== undefined) {
    if (downlink >= 10) {
      score = Math.min(100, score + 0);
    } else if (downlink >= 5) {
      score = Math.max(0, score - 5);
    } else if (downlink >= 1) {
      score = Math.max(0, score - 15);
    } else {
      score = Math.max(0, score - 30);
    }
  }

  if (rtt !== undefined) {
    if (rtt < 50) {
      score = Math.min(100, score + 5);
    } else if (rtt < 100) {
      score = Math.max(0, score + 0);
    } else if (rtt < 300) {
      score = Math.max(0, score - 10);
    } else {
      score = Math.max(0, score - 20);
    }
  }

  return Math.round(score);
}


